import { useMemo } from 'react';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';

// project imports
import AppBarStyled from './AppBarStyled';
import HeaderContent from './HeaderContent';
import IconButton from 'components/@extended/IconButton';

import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { DRAWER_WIDTH, MINI_DRAWER_WIDTH } from 'config';

// assets
import MenuFoldOutlined from '@ant-design/icons/MenuFoldOutlined';
import MenuUnfoldOutlined from '@ant-design/icons/MenuUnfoldOutlined';

// ==============================|| MAIN LAYOUT - HEADER ||============================== //

export default function Header() {
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  // header content
  const headerContent = useMemo(() => <HeaderContent />, []);

  // common header
  const mainHeader = (
    <Toolbar
      sx={{
        // White text inside the toolbar (the visible indigo bar)
        '& .MuiTypography-root': { color: '#fff' },
        '& .MuiSvgIcon-root': { color: '#fff' },
        '& .anticon': { color: '#fff' },
        '& .MuiButtonBase-root': { color: '#fff' },
        // Profile button styling — the pill with avatar + name
        '& .MuiButtonBase-root[aria-haspopup="true"]': {
          bgcolor: 'rgba(255,255,255,0.18)',
          backdropFilter: 'blur(8px)',
          borderRadius: '24px',
          px: 1.5,
          py: 0.5,
          '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' }
        }
      }}
    >
      <IconButton
        aria-label="open drawer"
        onClick={() => handlerDrawerOpen(!drawerOpen)}
        edge="start"
        color="secondary"
        variant="light"
        sx={{
          color: '#fff',
          bgcolor: drawerOpen ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.22)',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.32)' },
          ml: { xs: 0, lg: -2 }
        }}
      >
        {!drawerOpen ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
      </IconButton>
      {headerContent}
    </Toolbar>
  );

  const appBar = {
    position: 'fixed',
    color: 'inherit',
    elevation: 0,
    sx: {
      borderBottom: 'none',
      zIndex: 1200,
      width: { xs: '100%', lg: drawerOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : `calc(100% - ${MINI_DRAWER_WIDTH}px)` },
      // Indigo + pink gradient matching the login page
      background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 50%, #312e81 100%)',
      boxShadow: '0 2px 12px rgba(49, 46, 129, 0.25)'
    }
  };

  return (
    <>
      {/* GLOBAL OVERRIDE — forces dark text inside any popover/popper/menu rendered outside the toolbar */}
      <Box
        component="style"
        dangerouslySetInnerHTML={{
          __html: `
            /* Catch all popover/popper/menu variants */
            .MuiPopper-root .MuiTypography-root,
            .MuiPopover-root .MuiTypography-root,
            .MuiMenu-root .MuiTypography-root,
            .MuiMenuItem-root .MuiTypography-root,
            .MuiPopper-root h1, .MuiPopper-root h2, .MuiPopper-root h3,
            .MuiPopper-root h4, .MuiPopper-root h5, .MuiPopper-root h6,
            .MuiPopover-root h1, .MuiPopover-root h2, .MuiPopover-root h3,
            .MuiPopover-root h4, .MuiPopover-root h5, .MuiPopover-root h6,
            .MuiPopper-root p, .MuiPopper-root span:not(.MuiSwitch-thumb):not(.MuiSwitch-track),
            .MuiPopover-root p, .MuiPopover-root span:not(.MuiSwitch-thumb):not(.MuiSwitch-track) {
              color: rgba(0, 0, 0, 0.87) !important;
            }

            /* Secondary text — captions, body2, role labels */
            .MuiPopper-root .MuiTypography-caption,
            .MuiPopper-root .MuiTypography-body2,
            .MuiPopover-root .MuiTypography-caption,
            .MuiPopover-root .MuiTypography-body2 {
              color: rgba(0, 0, 0, 0.6) !important;
            }

            /* Icons inside dropdowns — keep their own colors (don't force black) */
            /* But ensure ListItem text is dark */
            .MuiPopper-root .MuiListItemText-root .MuiTypography-root,
            .MuiPopover-root .MuiListItemText-root .MuiTypography-root {
              color: rgba(0, 0, 0, 0.87) !important;
            }

            /* Tabs in dropdown */
            .MuiPopper-root .MuiTab-root,
            .MuiPopover-root .MuiTab-root {
              color: rgba(0, 0, 0, 0.6) !important;
            }
            .MuiPopper-root .MuiTab-root.Mui-selected,
            .MuiPopover-root .MuiTab-root.Mui-selected {
              color: #4f46e5 !important;
            }

            /* Buttons inside the dropdown — primary indigo */
            .MuiPopper-root .MuiButton-text,
            .MuiPopover-root .MuiButton-text {
              color: #4f46e5 !important;
            }

            /* IconButton inside dropdown — dark icons, indigo on hover */
            .MuiPopper-root .MuiIconButton-root,
            .MuiPopover-root .MuiIconButton-root {
              color: rgba(0, 0, 0, 0.54) !important;
            }
            .MuiPopper-root .MuiIconButton-root .MuiSvgIcon-root,
            .MuiPopover-root .MuiIconButton-root .MuiSvgIcon-root,
            .MuiPopper-root .MuiIconButton-root .anticon,
            .MuiPopover-root .MuiIconButton-root .anticon {
              color: rgba(0, 0, 0, 0.54) !important;
            }
            .MuiPopper-root .MuiIconButton-root:hover,
            .MuiPopover-root .MuiIconButton-root:hover {
              color: #4f46e5 !important;
            }
          `
        }}
      />
      {!downLG ? (
        <AppBarStyled open={drawerOpen} {...appBar}>
          {mainHeader}
        </AppBarStyled>
      ) : (
        <AppBar {...appBar}>{mainHeader}</AppBar>
      )}
    </>
  );
}